import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { graphql } from 'babel-plugin-relay/macro';
import { List, ListProps, Typography } from 'embeds/components';
import { compact, filter, find, get, groupBy, includes, isEmpty, map, uniqBy } from 'lodash';

import { useCurrentCountry } from '@lib/hooks';
import { Country } from '@lib/providers/LanguageProvider';

import {
  ListPatientContacts_EmbedPatient,
  ListPatientContacts_EmbedPatient$key,
} from './__generated__/ListPatientContacts_EmbedPatient.graphql';
import ListItemPatientContact from './ListItemPatientContact';

const messages = defineMessages({
  default: {
    id: 'deprecated.fragments.ListPatientContacts.default',
    defaultMessage: 'Default',
  },
  sms: {
    id: 'deprecated.fragments.ListPatientContacts.sms',
    defaultMessage: 'SMS',
  },
  email: {
    id: 'deprecated.fragments.ListPatientContacts.email',
    defaultMessage: 'Email',
  },
  whatsapp: {
    id: 'deprecated.fragments.ListPatientContacts.whatsapp',
    defaultMessage: 'WhatsApp',
  },
});

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    marginBottom: {
      marginBottom: unit,
    },
  });

// TODO move to TS utils file
export type ReadonlyArrayType<T extends ReadonlyArray<unknown>> = T extends ReadonlyArray<infer ElementType>
  ? ElementType
  : never;
export type PatientContact = ReadonlyArrayType<NonNullable<ListPatientContacts_EmbedPatient['contact']>>;

export interface ListPatientContactsProps extends Omit<ListProps, 'classes'>, WithStyles<typeof styles> {
  fragmentRef: ListPatientContacts_EmbedPatient$key;
  selectedContacts?: PatientContact[];
  onContactsSelected: (patientContacts?: PatientContact[]) => void;
}

const sanitizeContacts = (contacts: PatientContact[] | ListPatientContacts_EmbedPatient['contact'] | undefined) =>
  uniqBy(
    map(compact(contacts), (contact) => ({
      ...contact,
      id: `${contact.type}-${contact.value}`,
    })),
    'id',
  );

const ListPatientContacts: React.FC<ListPatientContactsProps> = ({
  classes,
  fragmentRef,
  selectedContacts,
  onContactsSelected,
  ...rest
}) => {
  const patient = useFragment<ListPatientContacts_EmbedPatient$key>(
    graphql`
      fragment ListPatientContacts_EmbedPatient on Patient {
        doNotContact
        contact {
          active
          type
          value
          ...ListItemPatientContact_EmbedPatientContact
        }
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const country = useCurrentCountry();

  const patientContactsWithId = sanitizeContacts(patient.contact);
  const selectedContactsWithId = sanitizeContacts(selectedContacts);

  const types = country === Country.US ? ['sms', 'email'] : ['sms', 'whatsapp', 'email'];
  const allContacts = filter(patientContactsWithId, (contact) => includes(types, contact.type));

  const contactGroups = groupBy(allContacts, 'type');

  const handleContactCheck = (patientContact: PatientContact, checked: boolean) => {
    // selectedContacts `undefined` in case we're using default contacts
    const prevSelectedContacts = selectedContactsWithId || filter(allContacts, 'active');
    const newContactsSelected = checked
      ? [...prevSelectedContacts, patientContact]
      : filter(prevSelectedContacts, (selectedContact) => get(selectedContact, 'id') !== get(patientContact, 'id'));
    onContactsSelected(isEmpty(newContactsSelected) ? [] : newContactsSelected);
  };

  return (
    <>
      {map(contactGroups, (contacts, type: keyof typeof messages) => (
        <div key={type} className={classes.marginBottom}>
          <Typography variant="secondarySectionHeader" gutterBottom>
            {formatMessage(messages[type])}
          </Typography>
          <List disablePadding includeBorder {...rest}>
            {map(contacts, (contact) => (
              <ListItemPatientContact
                key={contact.id as string}
                fragmentRef={contact}
                button
                dense
                disablePadding
                disabled={allContacts.length === 1}
                checked={selectedContactsWithId ? !!find(selectedContactsWithId, { id: contact.id }) : !!contact.active}
                onCheck={(event, checked) => handleContactCheck(contact, checked)}
              />
            ))}
          </List>
        </div>
      ))}
    </>
  );
};

export default withStyles(styles)(ListPatientContacts);
